import { handleJsonResponse, handleFileUploadResponse, getFilterQuery, addSortingQuery, getXsrfToken } from "./api";
import config from '../config'; 
import { getUser } from '../auth';
import { upload } from "@testing-library/user-event/dist/upload";

// Numeric type map for file action API
// The API expects a numeric "type" value, not a string
// Numeric type map matching C# FileActionTypes enum exactly
const FileActionTypeMap = {
  Uploading: 0,
  UploadComplete: 1,
  UploadFailed: 2,
};

async function getFileById (id, token) {
  const response = await fetch(`${config.API_URL}/files/${id}`,
  {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  return await handleJsonResponse(response);
}

export async function getFilesById(fileIds) {
  const user = getUser();
  const token = user?.access_token;
  try {
    const fetchFilesPromises = fileIds.map(id => getFileById(id, token));
    return await Promise.all(fetchFilesPromises);
  } catch (error) {
      console.error("Failed to fetch files:", error);
    return [];
  }

}

export async function getFiles(filters, sorting, start = 0, limit = 10) {
  const user = getUser();
  const token = user?.access_token;

  let filterQuery = getFilterQuery(filters);
  
  if (filterQuery.length > 0){
    filterQuery = "filter=" + filterQuery;
  }
  filterQuery = addSortingQuery(filterQuery, sorting);
  const response = await fetch(`${config.API_URL}/files?${filterQuery}start=${start}&limit=${limit}`,    
  {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  return await handleJsonResponse(response);
}


export async function downloadFile(fileData, updateProgress, downloadId, downloadSignal) {
  
  const user = getUser();
  const token = user?.access_token;
  
  
  try {
    const response = await fetch(`${config.API_URL}/files/${fileData.id}/data`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Status Code: ${response.status} ${response.statusText}`);
    }

   // Get file info from headers
    const contentLength = response.headers.get('Content-Length');
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition 
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
      : fileData.fileName;

   // Stream the response for large files
    const reader = response.body.getReader();
    const chunks = [];
    let receivedLength = 0;

    while (true) {
      if (downloadSignal?.aborted) {
        await reader.cancel();
        throw new DOMException("Download canceled by user", "AbortError");
      }
      const { done, value } = await reader.read();
     
      if (done) break;
      
      chunks.push(value);
      receivedLength += value.length;
      
     // Report progress if callback provided
      if (updateProgress && contentLength) {
        const progress = (receivedLength / parseInt(contentLength)) * 100;
        updateProgress(downloadId, progress);
      }
     
    }

   // Create blob directly from chunks (avoids allocating a single large buffer)
    const blob = new Blob(chunks, { type: response.headers.get('Content-Type') });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    
   // Cleanup after a delay
    setTimeout(() => {
      link.remove();
      URL.revokeObjectURL(blobUrl);
    }, 100);

    return { status: "success", message: "File downloaded successfully", id: fileData.id };
  } catch (error) {
    if (error instanceof DOMException) {
      return { status: "warning", message: "Download canceled by user", id: fileData.id, download_id :downloadId };
    }
   return { status: "error", message: `Error downloading file: ${error.message}`, id: fileData.id, download_id :downloadId };
  }
}


export async function downloadFiles(filesInfo, updateProgress) {
  const fileResponses = filesInfo.map((fileInfo) => 
  {
    return downloadFile(fileInfo.file, updateProgress, fileInfo.download_id);
  });
  return fileResponses;
}

export async function deleteFile(fileId) {
  const user = getUser();
  const token = user?.access_token;
  return await changeState(fileId, "archive", token);
}

// Upload multiple files using SAS URLs
export async function uploadFiles(toBeUploadedFiles, updateStatus, updateMessage, jobId) {
  
  const user = getUser();
  const token = user?.access_token;

  const results = await Promise.all(toBeUploadedFiles.map(async (toBeUploaded, index) => {
    let fileId = "";
    try {
      const uploadData = {
        Mimetype: toBeUploaded.file.type === '' ? "application/octet-stream" : toBeUploaded.file.type,
        FileSize: toBeUploaded.file.size,
        FileName: toBeUploaded.file.name,
        SupportedMethods: ["OutOfBandHttpPost"]
      };
      updateStatus(index, "Pending");

      const response = await fetch(`${config.API_URL}/files/inputs/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getXsrfToken()
        },
        body: JSON.stringify(uploadData)
      });
      
      
      const newFileResponse = await handleJsonResponse(response);
      fileId = newFileResponse.id;

      const uploadingStatusResponse = await changeState(fileId, "Uploading", token); 
      updateMessage(index, `Preparing: ${uploadingStatusResponse?.fileName || toBeUploaded.file.name}`);

      let putResponse;
      try {
        putResponse = await fetch(`${newFileResponse.connectionString}`, { 
          method: 'PUT',
          body: toBeUploaded.file,
          headers: {  
            'Content-Type': toBeUploaded.file.type || 'application/octet-stream',
            'x-ms-blob-type': "BlockBlob", 
          }, 
        });
      } catch (putError) {

        await changeState(fileId, "UploadFailed", token).catch(() => {});
        updateStatus(index, "error");
        updateMessage(index, `Upload failed: ${putError.message}. Check Azure CORS configuration.`);
        return { status: "error", message: putError.message };

      }

      const uploadFileResponse = await handleFileUploadResponse(putResponse);
      updateMessage(index, `Transferring: ${uploadFileResponse?.status || 'In progress'}`);
      updateStatus(index, "uploadComplete");

      const statusChange = await changeState(fileId, "UploadComplete", token);
      updateMessage(index, `Complete: ${statusChange?.fileName || toBeUploaded.file.name}`);

      if (jobId) {
        try {
          const linkResponse = await fetch(`${config.API_URL}/jobs/${jobId}`, {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json-patch+json',
            },
            body: JSON.stringify([
              {
                op: "add",
                path: "/files/-",
                value: fileId
              }
            ])
          });

          if (!linkResponse.ok) {
            // Log warning but do not throw — upload itself succeeded
            const errorBody = await linkResponse.json().catch(() => ({}));
            console.warn(`Failed to link file ${fileId} to job ${jobId}:`, errorBody);
          }
        } catch (linkError) {
          // Network-level failure — log and continue
          console.warn(`Error linking file ${fileId} to job ${jobId}:`, linkError.message);
        }
      }

      return { status: "success", message: `File uploaded successfully ${fileId}`, id: fileId };

    } catch(error) {
      let message = "Error uploading file. No id returned";
      if(fileId) {
        await changeState(fileId, "UploadFailed", token).catch(() => {});
        message = "Error uploading file";
      }
      updateStatus(index, "error");
      updateMessage(index, message);

      return { status: "error", message: message };
    }
      
  }));
  
  return results;  
}

export async function changeState(fileId, state, token) {
  // Map string state name to numeric value expected by the API
  const typeValue = typeof state === 'number' ? state : (FileActionTypeMap[state] ?? state);
  const data = JSON.stringify({ type: typeValue });
  const response = await postFileAction(fileId, data, token);
  return await handleJsonResponse(response);
}

export async function postFileAction(fileId, data, token) {
  const body = typeof data === 'string' ? data : JSON.stringify(data);
  const response = await fetch(`${config.API_URL}/files/${fileId}/actions`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': getXsrfToken()
    },
    body
  });
  
  return response;
}